import React, { useState, useEffect } from 'react';
import { Checkbox, MenuItem, OutlinedInput, InputLabel, InputAdornment, FormControl, Box, IconButton, Select, Typography, Avatar, AvatarGroup, Tooltip } from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox, Circle, Clear } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const RenderOption = ({ topic, topics, onClickCheckbox }) => {
    const selected = Boolean(topics.find(e => e.id === topic.id));

    return (
        <MenuItem
            value={topic}
            onClick={() => onClickCheckbox({ topic, selected })}
            sx={{ padding: 0, paddingRight: 1 }}
        >
            <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                sx={{ marginRight: 1 }}
                checked={selected}
            />
            <Circle fontSize="small" sx={{ color: topic.colour ?? 'rgba(0, 0, 0, 0.08)', marginRight: 1 }} />
            <Typography variant="inherit" noWrap>
                {topic.name}
            </Typography>
        </MenuItem>
    );
};

const RenderValues = values => {
    const theme = useTheme();

    if (!values || values.length === 0) {
        return null;
    }

    return (
        <AvatarGroup
            max={6}
            sx={{
                justifyContent: 'flex-end'
            }}
            slotProps={{
                additionalAvatar: {
                    sx: {
                        width: 24,
                        height: 24,
                        backgroundColor: 'transparent',
                        color: theme.palette.text.secondary,
                        '&.MuiAvatar-root': {
                            border: 0,
                            ml: 0.5,
                            alignSelf: 'center'
                        }
                    }
                }
            }}
        >
            {values.map(e => (
                <Tooltip title={e.name} key={e.id}>
                    <Avatar
                        alt={e.name}
                        sx={{
                            width: 24,
                            height: 24,
                            bgcolor: e.colour ?? undefined
                        }}
                    >
                        {e.name.substring(0, 1)}
                    </Avatar>
                </Tooltip>
            ))}
        </AvatarGroup>
    );
};

const TopicFilter = ({ availableTopics, selectedTopics, onRemoveTopic, onAddTopic, onClearTopics }) => {
    const [topics, setTopics] = useState(selectedTopics);

    const handleClearFilter = () => {
        onClearTopics();
        setTopics([]);
    };

    const handleClickCheckbox = ({ topic, selected }) => {
        if (selected) {
            onRemoveTopic(topic);
            setTopics(topics.filter(e => e.id !== topic.id));
        }
        else {
            onAddTopic(topic);

            const newTopics = [...topics, topic];

            const orderedTopics = newTopics.sort((a, b) => {
                const nameA = a.name.toLowerCase();
                const nameB = b.name.toLowerCase();

                if (nameA < nameB) {
                    return -1;
                }

                if (nameA > nameB) {
                    return 1;
                }

                return 0;
            });

            setTopics(orderedTopics);
        }
    };

    useEffect(() => {
        setTopics(selectedTopics);
    }, [selectedTopics]);

    const disabled = !availableTopics || availableTopics.length === 0;
    const label = disabled ? 'No Topics available' : 'Filter by Topic';

    return (
        <FormControl
            sx={{
                margin: 0,
                marginRight: 1,
                width: 212 //annoying to have to give this a width, but without one it is collapsed
            }}
            size="small"
            disabled={disabled}
        >
            <InputLabel id="topicFilterLabel">{label}</InputLabel>
            <Select
                labelId="topicFilterLabel"
                id="topicFilter"
                multiple
                value={topics}
                input={(
                    <OutlinedInput
                        label={label}
                    />
                )}
                renderValue={RenderValues}
                IconComponent={() => null}
                sx={{
                    paddingRight: 0
                }}
                endAdornment={disabled ? undefined : (
                    <InputAdornment position="end">
                        <IconButton
                            onClick={handleClearFilter}
                            disabled={topics.length === 0}
                        >
                            <Clear />
                        </IconButton>
                    </InputAdornment>
                )}
            >
                <Box
                    sx={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                            width: '6px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                            borderRadius: '3px'
                        }
                    }}
                >
                    {availableTopics?.map(topic => (
                        <RenderOption
                            key={topic.id}
                            topic={topic}
                            topics={topics}
                            onClickCheckbox={handleClickCheckbox}
                        />
                    ))}
                </Box>
            </Select>
        </FormControl>
    );
};

export default TopicFilter;